import type { CanvasObject, CanvasObjectType, SectionCanvasObject } from "./canvas-object";

// セクション（領域コンテナ）のドメイン定数と包含判定ロジック。
// 親子関係は永続化せず、「セクション移動時にどのオブジェクトを一緒に動かすか」を
// ドラッグ開始時にこの判定で幾何計算する（サーバスキーマ・移動 op の変更が不要で、
// 既存のクライアント駆動グループ移動と整合するため）。

/** クリック配置時の既定サイズ（幅）。 */
export const SECTION_DEFAULT_W = 960;
/** クリック配置時の既定サイズ（高さ）。 */
export const SECTION_DEFAULT_H = 540;
/** リサイズ・ドラッグ配置時の最小サイズ（幅）。タイトルバーが潰れない下限として設定。 */
export const SECTION_MIN_W = 200;
/** リサイズ・ドラッグ配置時の最小サイズ（高さ）。 */
export const SECTION_MIN_H = 120;
/** fill 未設定時に描画へ使う既定の背景色（薄いグレー。塗りはレンダラ側で半透明化する）。 */
export const SECTION_DEFAULT_FILL = "#8993a4";

/** 作成時の既定タイトル。 */
export const SECTION_DEFAULT_TITLE = "セクション";

/**
 * セクション移動時に一緒に移動する内容物の id を返す。
 *
 * 判定規則（設計文書 §3-6）:
 * - オブジェクトの AABB（x/y/w/h）がセクション矩形に**完全に含まれる**こと。
 *   端に重なっただけのオブジェクトを巻き込まず、視覚的な「中に入っている」と一致させる。
 * - comment-thread（専用オーバーレイのピン）と connector（端点追従に委ねる）は対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - セクション自身は対象外。ネストされた別セクションは完全包含なら追従する。
 *
 * 回転は考慮しない（セクションは回転非対応。内容物が回転していても保存 AABB は
 * x/y/w/h のまま
 * であり、既存のグループ移動・マーキーと同じ近似に揃える）。
 */
export function collectSectionContainedObjectIds(
  objects: Iterable<CanvasObject>,
  section: SectionCanvasObject
): string[] {
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === section.id) continue;
    if (object.type === "comment-thread" || object.type === "connector") continue;
    if (object.locked === true) continue;
    if (isAabbContained(object, section)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * オブジェクト移動時に「上に載っている」オブジェクトの id を返す（セクション挙動の全オブジェクトへの一般化）。
 *
 * セクションだけでなく任意のオブジェクトを移動する際、その矩形へ**完全に含まれ**、かつ
 * mover **より前面（描画順で後ろ＝上に載っている）** に描かれるオブジェクトを一緒に動かす。
 * これにより「付箋や図形の上にすっぽり収まったスタンプ等」が、背面の図形の移動に追従する。
 *
 * 判定規則:
 * - mover がセクションの場合は従来どおり {@link collectSectionContainedObjectIds} に委譲する
 *   （セクションは常に最背面帯に描かれ、完全包含した内容物は必ず前面のため、前面判定を加えても
 *   結果は変わらない。既存のセクション挙動・ネスト挙動を厳密に維持する）。
 * - mover がセクション以外の場合は、完全包含に加えて {@link compareCanvasObjectsForRender} で
 *   mover より前面に描画されるものだけを対象にする。少しでもはみ出したオブジェクト、および
 *   mover より背面（下）にあるオブジェクトは対象外（置いていかれる）。
 * - comment-thread（専用オーバーレイのピン）と connector（端点追従に委ねる）は対象外。
 * - locked のオブジェクトは追従しない（ロック＝位置固定の意図を優先する）。
 * - mover 自身は対象外。
 *
 * 回転は考慮しない（保存 AABB は x/y/w/h のまま。既存のグループ移動・マーキー・セクションと同じ近似）。
 */
export function collectObjectsCarriedByMove(
  objects: Iterable<CanvasObject>,
  mover: CanvasObject
): string[] {
  if (mover.type === "section") {
    return collectSectionContainedObjectIds(objects, mover);
  }
  const result: string[] = [];
  for (const object of objects) {
    if (object.id === mover.id) continue;
    if (object.type === "comment-thread" || object.type === "connector") continue;
    if (object.locked === true) continue;
    // mover より前面（上に載っている）ものだけを追従対象にする。背面（下）のオブジェクトや
    // 描画順が同一以下のものは巻き込まない。描画順の判定は実際の重なり順と同じ規則を共有する。
    if (compareCanvasObjectsForRender(mover, object) >= 0) continue;
    if (isAabbContained(object, mover)) {
      result.push(object.id);
    }
  }
  return result;
}

/**
 * 描画バンド。セクションは通常オブジェクトより常に背面に描画するため、
 * zOrder 比較の前段でバンド（0 = セクション帯 / 1 = 通常帯）を比較する。
 * Web レンダリング（サブレイヤ分離）とサムネイル / PDF（描画順）で同じ規則を共有する。
 */
export function canvasObjectRenderBand(type: CanvasObjectType): 0 | 1 {
  return type === "section" ? 0 : 1;
}

/**
 * 描画順の比較関数。バンド（セクション帯が先）→ zOrder → id（同値時の安定化）の順で比較する。
 */
export function compareCanvasObjectsForRender(a: CanvasObject, b: CanvasObject): number {
  const bandDiff = canvasObjectRenderBand(a.type) - canvasObjectRenderBand(b.type);
  if (bandDiff !== 0) return bandDiff;
  if (a.zOrder !== b.zOrder) return a.zOrder - b.zOrder;
  return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
}

interface Aabb {
  readonly x: number;
  readonly y: number;
  readonly w: number;
  readonly h: number;
}

/**
 * セクションは回転非対応（設計判断 #8）。保存前にセクションの rotation を取り除く。
 * transform 経路（op-applier / canvas-object-service）では rotation を strip しているが、
 * create 経路では範囲チェックしかしないため、細工クライアントが `type: "section", rotation: 45`
 * を送ると回転値が永続化され、本体は無回転表示なのに選択枠・PDF・サムネイルだけ回転する
 * 不整合になり得る。これを防ぐため、create 経路（WS / REST / MCP の prepareObjectForCreate）で
 * この関数を通す。section 以外・rotation 未設定はそのまま返す。
 */
export function stripSectionRotation(object: CanvasObject): CanvasObject {
  if (object.type !== "section" || object.rotation === undefined) return object;
  const { rotation: _ignoredRotation, ...rest } = object;
  return rest;
}

/** inner の AABB が outer の AABB に完全に含まれるか（境界一致は「含まれる」とみなす）。 */
export function isAabbContained(inner: Aabb, outer: Aabb): boolean {
  return (
    inner.x >= outer.x &&
    inner.y >= outer.y &&
    inner.x + inner.w <= outer.x + outer.w &&
    inner.y + inner.h <= outer.y + outer.h
  );
}
